import { FeaturePermissions, useAppSelector, UserVoice, useUserInfo, useUserVoices, VoiceStatus } from "@next-platform/client-common-core";
import { CapabilityKey, useApplicationCapabilities } from "@next-platform/client-common-core/infra/application/capabilities";
import { Gender } from "@next-platform/packages-ts-types";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import useAuth from "@/services/useAuth";

import { getVoiceById, priortizeVoiceId, transformVoices } from "../../utils";
import { VoiceButtonVariant, VoiceUpgradeVariant } from "../ChatBubble/types";
import { ChatPageModal } from "../constants";
import { PREFERRED_VOICE_ACTIONS, PREMIUM_PLAN_ROUTE } from "../voices/constant";
import { IChatMessageAudio, IChatVoice, PREFFERRED_VOICE_API_ACTIONS, VoiceCategories } from "../voices/types";

let audio = new Audio();
const stopAudioPlay = () => {
  audio.src = "";
};

const useChatMessageAudio = ({
  currentAudioMessageId,
  messageId,
  voiceId,
  conversationId,
  voiceSelectionMode = false,
  showVoiceSelectionModal,
  isAudioLoading,
  characterId,
  defaultPersonaId,
  loadMessages,
  handlePreferredVoiceActions,
  handleModalVisibility,
  handleVoiceButtonClick,
}: IChatMessageAudio) => {
  const navigate = useNavigate();
  const { isAuthenticated, register } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<VoiceCategories>(VoiceCategories.ALL);
  const [voices, setVoices] = useState<IChatVoice[]>([]);
  const [defaultVoiceId, setDefaultVoiceId] = useState(voiceId);
  const [voiceSelectionLoading, setVoiceSelectionLoading] = useState(false);
  const [showVoiceSelectionInVoiceButton, setShowVoiceSelectionInVoiceButton] = useState(false);
  const { permissions } = useUserInfo();
  const allVoices = useAppSelector((state) => state?.voices?.allVoices);
  const { userVoices, getUserVoices } = useUserVoices();
  const [transformedAllVoices, setTransformedAllVoices] = useState<IChatVoice[]>([]);
  const ttsCapability = useApplicationCapabilities(CapabilityKey.Tts);
  const isTtsAllowed = ttsCapability?.allowed ?? false;
  const hasCustomVoicesAccess = permissions?.includes(FeaturePermissions.TTS_CUSTOM_VOICES) ?? false;
  const { hasFeatureFlag: hasCustomVoiceFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.TTS);
  const canUseCustomVoices = hasCustomVoiceFeatureFlag && hasCustomVoicesAccess;
  const voiceUpgradeVariant: VoiceUpgradeVariant = isAuthenticated ? VoiceUpgradeVariant.MEMBER : VoiceUpgradeVariant.GUEST;
  const conversationVoiceInitialStatus = isTtsAllowed && voiceId ? VoiceButtonVariant.PLAY : VoiceButtonVariant.IDLE;

  const [chatVoiceButtonVariant, setChatVoiceButtonVariant] = useState(conversationVoiceInitialStatus);
  const isAudioLoadingForCurrentMessage = isAudioLoading && currentAudioMessageId === messageId;

  const updateChatVoiceButtonVariant = () => {
    const isMessageAudioPlaying = currentAudioMessageId === messageId;
    let expectedVariant: VoiceButtonVariant;
    if (!isTtsAllowed || !voiceId) {
      expectedVariant = VoiceButtonVariant.IDLE;
    } else if (isMessageAudioPlaying && !isAudioLoadingForCurrentMessage) {
      expectedVariant = VoiceButtonVariant.STOP;
    } else {
      expectedVariant = VoiceButtonVariant.PLAY;
    }
    setChatVoiceButtonVariant(expectedVariant);
  };

  const handleSuccess = (apiAction: PREFFERRED_VOICE_API_ACTIONS) => {
    switch (apiAction) {
      case PREFFERRED_VOICE_API_ACTIONS.SAVE_CONVERSATION_VOICE_ID: {
        setChatVoiceButtonVariant(VoiceButtonVariant.PLAY);
        closeVoiceSelectionModal();
        setVoiceSelectionLoading(false);
        setDefaultVoiceId(voiceId);
      }
    }
  };

  const handleError = (apiAction: PREFFERRED_VOICE_API_ACTIONS) => {
    switch (apiAction) {
      case PREFFERRED_VOICE_API_ACTIONS.GET_ALL_VOICES: {
        closeVoiceSelectionModal();
        break;
      }
      case PREFFERRED_VOICE_API_ACTIONS.SAVE_CONVERSATION_VOICE_ID: {
        setDefaultVoiceId(voiceId);
        setVoiceSelectionLoading(false);
        break;
      }
    }
  };
  useEffect(() => {
    updateChatVoiceButtonVariant();
  }, [isTtsAllowed, voiceId, currentAudioMessageId, isAudioLoadingForCurrentMessage]);

  useEffect(() => {
    if (showVoiceSelectionModal && voiceSelectionMode) {
      handlePreferredVoiceActions({
        action: PREFERRED_VOICE_ACTIONS.Get_Voices,
        errorCB: () => handleError(PREFFERRED_VOICE_API_ACTIONS.GET_ALL_VOICES),
      });
      if (canUseCustomVoices) {
        void getUserVoices();
      }
    }
  }, [canUseCustomVoices, getUserVoices, handlePreferredVoiceActions, showVoiceSelectionModal, voiceSelectionMode]);

  const closeVoiceSelectionModal = () => {
    setShowVoiceSelectionInVoiceButton?.(false);
  };

  const queuePlaybackAfterMessagesLoad = async () => {
    await loadMessages();
    handleVoiceButtonClick("");
  };

  const createConversationWithCurrentVoice = () => {
    if (!voiceId) {
      return;
    }

    handlePreferredVoiceActions({
      action: PREFERRED_VOICE_ACTIONS.Set_Conversation_Voice_Id,
      selectedVoiceId: voiceId,
      characterId,
      defaultPersonaId,
    });
  };

  const handlePlayAction = async () => {
    if (conversationId && !messageId) {
      await queuePlaybackAfterMessagesLoad();
      return;
    }
    if (!conversationId && !messageId) {
      createConversationWithCurrentVoice();
      return;
    }
    if (!messageId) {
      return;
    }
    handlePreferredVoiceActions({
      action: PREFERRED_VOICE_ACTIONS.Play,
      messageId,
    });
  };

  useEffect(() => {
    return () => {
      stopAudioPlay();
    };
  }, []);

  const filterVoicesByGender = (gender: Gender) => {
    return transformedAllVoices.filter((voice) => voice.gender === gender);
  };

  const transformedUserVoices = useMemo<IChatVoice[]>(
    () =>
      userVoices
        .filter((voice: UserVoice) => voice.status === VoiceStatus.READY_TO_USE)
        .map((voice: UserVoice) => ({
          id: voice.id,
          name: voice.name,
          label: voice.name,
          subLabel: voice.description,
          description: voice.description,
          audio_url: "",
          isCustom: true,
          onChange: (shouldPlay: boolean, reset: () => void) => {
            if (!shouldPlay) {
              stopAudioPlay();
              return;
            }

            reset();
          },
        })),
    [userVoices],
  );

  const allSelectableVoices = useMemo<IChatVoice[]>(
    () => (canUseCustomVoices ? [...transformedAllVoices, ...transformedUserVoices] : transformedAllVoices),
    [canUseCustomVoices, transformedAllVoices, transformedUserVoices],
  );

  const getVoices = (): IChatVoice[] => {
    let updatedVoices: IChatVoice[];
    if (selectedCategory === VoiceCategories.MALE) {
      updatedVoices = filterVoicesByGender(Gender.Male);
    } else if (selectedCategory === VoiceCategories.FEMALE) {
      updatedVoices = filterVoicesByGender(Gender.Female);
    } else if (selectedCategory === VoiceCategories.MY_VOICES) {
      updatedVoices = canUseCustomVoices ? transformedUserVoices : [];
    } else {
      updatedVoices = allSelectableVoices;
    }
    updatedVoices = priortizeVoiceId(updatedVoices, voiceId);
    return updatedVoices;
  };

  useEffect(() => {
    if (!allVoices?.length) {
      return;
    }
    setTransformedAllVoices(transformVoices(allVoices, playVoiceSample));
  }, [allVoices]);

  useEffect(() => {
    setVoices(getVoices());
  }, [allSelectableVoices, conversationId, selectedCategory, transformedAllVoices, transformedUserVoices]);

  // called when user clicks on voice/play/stop button in chat bubble
  const handleOnPress = async () => {
    if (isAudioLoadingForCurrentMessage) {
      return;
    }

    if (ttsCapability?.isPaywall) {
      return handleModalVisibility(ChatPageModal.SHOW_VOICE_UPGRADE_MODAL, true);
    }
    if (!isTtsAllowed) {
      return;
    }

    switch (chatVoiceButtonVariant) {
      case VoiceButtonVariant.IDLE:
        if (voiceId) {
          await handlePlayAction();
          return;
        }
        handlePreferredVoiceActions({
          action: PREFERRED_VOICE_ACTIONS.Get_Voices,
          errorCB: () => handleError(PREFFERRED_VOICE_API_ACTIONS.GET_ALL_VOICES),
        });
        setSelectedCategory(VoiceCategories.ALL);
        handleModalVisibility(ChatPageModal.SHOW_VOICE_SELECION_MODAL, true);
        handleVoiceButtonClick(messageId ?? "");
        break;
      case VoiceButtonVariant.PLAY:
        await handlePlayAction();
        break;
      case VoiceButtonVariant.STOP:
        handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
        break;
    }
  };

  const onPressUpgrade = () => {
    if (isAuthenticated) {
      return navigate(PREMIUM_PLAN_ROUTE);
    }
    register();
  };

  const onSelectVoiceId = async (selectedVoiceId: IChatVoice["id"]) => {
    if (!selectedVoiceId) {
      return;
    }
    setDefaultVoiceId(selectedVoiceId);
    stopAudioPlay?.();
    setVoiceSelectionLoading(true);
    const selectedVoiceName = getVoiceById(allSelectableVoices, selectedVoiceId)?.name ?? "";
    handlePreferredVoiceActions({
      action: PREFERRED_VOICE_ACTIONS.Set_Conversation_Voice_Id,
      selectedVoiceId,
      selectedVoiceName,
      characterId,
      defaultPersonaId,
      errorCB: () => handleError(PREFFERRED_VOICE_API_ACTIONS.SAVE_CONVERSATION_VOICE_ID),
      successCB: () => handleSuccess(PREFFERRED_VOICE_API_ACTIONS.SAVE_CONVERSATION_VOICE_ID),
    });
  };

  const playVoiceSample = (shouldPlay: boolean, onAudioEnded: () => void, audioUrl?: string) => {
    if (!shouldPlay) {
      stopAudioPlay();
      return;
    }
    if (!audioUrl) {
      return;
    }
    if (!audio) {
      audio = new Audio();
    }
    handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
    audio.src = audioUrl;
    audio.play();
    audio.removeEventListener("ended", onAudioEnded);
    audio.addEventListener("ended", onAudioEnded);
  };

  const onPressCategory = (category: VoiceCategories) => {
    setSelectedCategory(category);
    stopAudioPlay();
  };

  const handleOnOpenChangeVoiceSelectionModal = (isOpen: boolean) => {
    setShowVoiceSelectionInVoiceButton(isOpen);
    stopAudioPlay?.();
  };

  const actions = [
    {
      active: selectedCategory === VoiceCategories.ALL,
      label: VoiceCategories.ALL,
      onPress: () => onPressCategory(VoiceCategories.ALL),
    },
    {
      active: selectedCategory === VoiceCategories.MALE,
      label: VoiceCategories.MALE,
      onPress: () => onPressCategory(VoiceCategories.MALE),
    },
    {
      active: selectedCategory === VoiceCategories.FEMALE,
      label: VoiceCategories.FEMALE,
      onPress: () => onPressCategory(VoiceCategories.FEMALE),
    },
    ...(canUseCustomVoices
      ? [
          {
            active: selectedCategory === VoiceCategories.MY_VOICES,
            label: VoiceCategories.MY_VOICES,
            onPress: () => onPressCategory(VoiceCategories.MY_VOICES),
          },
        ]
      : []),
  ];

  return {
    handleOnPress,
    chatVoiceButtonVariant,
    isAudioLoadingForCurrentMessage,
    showVoiceSelectionInVoiceButton,
    handleOnOpenChangeVoiceSelectionModal,
    allVoices: allSelectableVoices,
    actions,
    voices,
    onSelectVoiceId,
    transformedAllVoices,
    stopAudioPlay,
    defaultVoiceId,
    voiceSelectionLoading,
    voiceUpgradeVariant,
    onPressUpgrade,
  };
};

export default useChatMessageAudio;
